import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

// Multi-file uploader. Calls onUploaded(items[]) where each item is {id, kind, url, caption}.
export default function MultiUploadButton({
  accept = "image/*,video/*,application/pdf",
  onUploaded,
  label = "Upload photos, videos or PDFs",
  testid = "multi-upload-button",
  size = "sm",
  variant = "outline",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const kind = (data.content_type || "").startsWith("image/")
      ? "image"
      : (data.content_type || "").startsWith("video/")
      ? "video"
      : "pdf";
    return { kind, url: data.url, caption: "" };
  };

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const results = [];
    for (const f of Array.from(files)) {
      try {
        const item = await uploadOne(f);
        results.push(item);
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "Upload failed"}`);
      }
    }
    if (results.length) {
      onUploaded?.(results);
      toast.success(`${results.length} file${results.length > 1 ? "s" : ""} uploaded`);
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} onClick={pick} disabled={busy} data-testid={testid} className="border-[#E8DEC8] text-[#5C061C]">
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
        {busy ? "Uploading..." : label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        onChange={(e) => upload(e.target.files)}
        className="hidden"
        data-testid={`${testid}-input`}
      />
    </>
  );
}
